import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { recruiterApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { FileText, AlertCircle, Calendar, Copy, CheckCircle2, Clock, Eye, Trash2, Loader2, Send, X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/Textarea'

interface SentForm {
  id: number
  uuid: string
  recruiter_uuid: string
  candidate_uuid: string
  candidate_name: string | null
  candidate_email: string | null
  job_description: string | null
  questions: Array<any>
  review: string | null
  score: number | null
  complete: number
  created_at: string
  updated_at: string
}

export default function SentFormsPage() {
  const { recruiter } = useAuth()
  const navigate = useNavigate()
  const [sentforms, setSentforms] = useState<SentForm[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedUuid, setCopiedUuid] = useState<string | null>(null)
  const [expandedReview, setExpandedReview] = useState<number | null>(null)
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null)
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)
  const [sentFormToDelete, setSentFormToDelete] = useState<SentForm | (SentForm & { id: 0 }) | null>(null)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  const [showSendToManagerDialog, setShowSendToManagerDialog] = useState(false)
  const [managers, setManagers] = useState<Array<{ id: number; name: string; email: string }>>([])
  const [selectedManagerId, setSelectedManagerId] = useState<number | ''>('')
  const [sendToManagerSubject, setSendToManagerSubject] = useState('שאלונים שמולאו – מערכת גיוס')
  const [sendToManagerBody, setSendToManagerBody] = useState('היי,\n\nמצורפים קישורים לשאלונים שמולאו:\n\n')
  const [isSendingToManager, setIsSendingToManager] = useState(false)
  const [isLoadingManagers, setIsLoadingManagers] = useState(false)

  const fetchSentForms = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await recruiterApi.getSentForms()
      
      if (response.status === 'success') {
        setSentforms(response.sentforms)
      } else {
        setError('שגיאה בטעינת השאלונים שנשלחו')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בטעינת השאלונים שנשלחו')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSentForms()
  }, [recruiter])

  useEffect(() => {
    if (showSendToManagerDialog) {
      setIsLoadingManagers(true)
      recruiterApi.getProfessionalManagers().then((res) => {
        if (res.status === 'success') {
          const list = res.professional_managers
          setManagers(list)
          if (list.length > 0) setSelectedManagerId(list[0].id)
        }
        setIsLoadingManagers(false)
      }).catch(() => setIsLoadingManagers(false))
    }
  }, [showSendToManagerDialog])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return '—'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const handleCopyUuid = async (uuid: string) => {
    try {
      await navigator.clipboard.writeText(uuid)
      setCopiedUuid(uuid)
      setTimeout(() => setCopiedUuid(null), 2000)
    } catch (err) {
      console.error('Failed to copy UUID:', err)
    }
  }

  const toggleReview = (id: number) => {
    setExpandedReview(expandedReview === id ? null : id)
  }

  const toggleSelect = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedUuids.size === sentforms.length) {
      setSelectedUuids(new Set())
    } else {
      setSelectedUuids(new Set(sentforms.map((f) => f.uuid)))
    }
  }

  const handleDeleteClick = (sentform: SentForm) => {
    setSentFormToDelete(sentform)
  }

  const handleBulkDeleteClick = () => {
    if (selectedUuids.size === 0) return
    setSentFormToDelete({ id: 0 } as SentForm & { id: 0 })
  }

  const handleDeleteConfirm = async () => {
    if (!sentFormToDelete) return
    const isBulk = sentFormToDelete.id === 0
    const uuids = isBulk ? Array.from(selectedUuids) : [sentFormToDelete.uuid]
    try {
      if (isBulk) setIsBulkDeleting(true)
      else setIsDeleting(true)
      setError(null)
      const res = await recruiterApi.deleteSentForms(uuids)
      if (res.status === 'success') {
        setSentforms((prev) => prev.filter((f) => !uuids.includes(f.uuid)))
        if (isBulk) setSelectedUuids(new Set())
        setSentFormToDelete(null)
      } else {
        setError(res.message || 'שגיאה במחיקה')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || (isBulk ? 'שגיאה במחיקת השאלונים' : 'שגיאה במחיקת השאלון'))
    } finally {
      setIsDeleting(false)
      setIsBulkDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setSentFormToDelete(null)
  }

  const isAllSelected = sentforms.length > 0 && selectedUuids.size === sentforms.length
  const isIndeterminate = selectedUuids.size > 0 && selectedUuids.size < sentforms.length

  const handleSendToManagerClick = () => {
    if (selectedUuids.size === 0) return
    setSelectedManagerId('')
    setShowSendToManagerDialog(true)
  }

  const handleSendToManager = async () => {
    if (!selectedManagerId || selectedUuids.size === 0) return
    try {
      setIsSendingToManager(true)
      setError(null)
      await recruiterApi.sendFormsToManager(
        selectedManagerId as number,
        Array.from(selectedUuids),
        sendToManagerSubject || undefined,
        sendToManagerBody || undefined,
        window.location.origin
      )
      setShowSendToManagerDialog(false)
      setSelectedUuids(new Set())
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בשליחה למנהל')
    } finally {
      setIsSendingToManager(false)
    }
  }

  const handleSendToManagerCancel = () => {
    setShowSendToManagerDialog(false)
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="bg-background p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">שאלונים שנשלחו</h1>
          <p className="text-muted-foreground mt-1">
            רשימת כל השאלונים שנשלחו למועמדים
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{sentforms.length} שאלונים</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sent Forms Table */}
      {sentforms.length === 0 && !error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">
                אין שאלונים שנשלחו
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                שאלונים יופיעו כאן לאחר שליחתם למועמדים
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>רשימת שאלונים שנשלחו</CardTitle>
              {selectedUuids.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="default">{selectedUuids.size} שאלונים נבחרו</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendToManagerClick}
                    disabled={isSendingToManager}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    שלח למנהל מקצועי
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDeleteClick}
                    disabled={isBulkDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    מחק נבחרים
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-right p-4 font-semibold w-12">
                      <Checkbox
                        checked={isIndeterminate ? ('indeterminate' as any) : isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="בחר הכל"
                      />
                    </th>
                    <th className="text-right p-4 font-semibold">מועמד</th>
                    <th className="text-right p-4 font-semibold">תיאור משרה</th>
                    <th className="text-right p-4 font-semibold">מספר שאלות</th>
                    <th className="text-right p-4 font-semibold">סטטוס</th>
                    <th className="text-right p-4 font-semibold">ציון</th>
                    <th className="text-right p-4 font-semibold">ביקורת</th>
                    <th className="text-right p-4 font-semibold">תאריך שליחה</th>
                    <th className="text-right p-4 font-semibold">מזהה</th>
                    <th className="text-right p-4 font-semibold w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {sentforms.map((sentform) => (
                    <tr 
                      key={sentform.id} 
                      className="border-b hover:bg-muted/50 transition-colors"
                      onMouseEnter={() => setHoveredRowId(sentform.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      <td className="p-4 w-12">
                        <Checkbox
                          checked={selectedUuids.has(sentform.uuid)}
                          onCheckedChange={() => toggleSelect(sentform.uuid)}
                          aria-label={`בחר שאלון ${sentform.candidate_name || sentform.uuid}`}
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {sentform.candidate_name || '—'}
                          </p>
                          {sentform.candidate_email && (
                            <p className="text-xs text-muted-foreground">
                              {sentform.candidate_email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="max-w-md">
                          <p className="text-sm text-gray-900">
                            {truncateText(sentform.job_description)}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="default">
                          {sentform.questions?.length || 0} שאלות
                        </Badge>
                      </td>
                      <td className="p-4">
                        {sentform.complete === 1 ? (
                          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            הושלם
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            בהמתנה
                          </Badge>
                        )}
                      </td>
                      <td className="p-4">
                        {sentform.score !== null && sentform.score !== undefined ? (
                          <span className="text-sm font-semibold">
                            {sentform.score.toFixed(1)}/10
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {sentform.review ? (
                          <div>
                            <p className="text-sm text-gray-900 mb-1">
                              {expandedReview === sentform.id 
                                ? sentform.review 
                                : truncateText(sentform.review, 80)}
                            </p>
                            {sentform.review.length > 80 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => toggleReview(sentform.id)}
                              >
                                {expandedReview === sentform.id ? 'הסתר' : 'הצג עוד'}
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{formatDate(sentform.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {sentform.uuid.substring(0, 8)}...
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleCopyUuid(sentform.uuid)}
                            title="העתק מזהה טופס"
                          >
                            <Copy className={`h-3 w-3 ${copiedUuid === sentform.uuid ? 'text-green-600' : ''}`} />
                          </Button>
                        </div>
                      </td>
                      <td className="p-4 w-16">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === sentform.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => navigate(`/sentform/${sentform.uuid}/candidate-details`)}
                            title="צפה בפרטי מועמד וביקורת"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === sentform.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => handleDeleteClick(sentform)}
                            disabled={isDeleting || isBulkDeleting}
                            title="מחק שאלון"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {sentFormToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                {sentFormToDelete.id === 0 ? 'מחיקת שאלונים' : 'מחיקת שאלון'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {sentFormToDelete.id === 0 ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    האם אתה בטוח שברצונך למחוק את {selectedUuids.size} השאלונים הנבחרים?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    פעולה זו לא ניתנת לביטול.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    האם אתה בטוח שברצונך למחוק את השאלון שנשלח ל־<strong>{sentFormToDelete.candidate_name || 'מועמד'}</strong>?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    פעולה זו לא ניתנת לביטול.
                  </p>
                </>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting || isBulkDeleting}
                >
                  ביטול
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting || isBulkDeleting}
                  className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                >
                  {(isDeleting || isBulkDeleting) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      מוחק...
                    </>
                  ) : (
                    'מחק'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send to professional manager dialog */}
      {showSendToManagerDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>שלח שאלונים שמולאו למנהל מקצועי</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSendToManagerCancel}
                  disabled={isSendingToManager}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                נבחרו {selectedUuids.size} שאלונים. בחר מנהל מקצועי שיקבל קישורים לשאלונים במייל.
              </p>
              <div>
                <label className="text-sm font-medium mb-2 block">מנהל מקצועי</label>
                {isLoadingManagers ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    טוען מנהלים...
                  </div>
                ) : managers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    אין מנהלים מקצועיים. הוסף מנהלים בעמוד &quot;מנהלים מקצועיים&quot;.
                  </p>
                ) : (
                  <select
                    value={selectedManagerId}
                    onChange={(e) => setSelectedManagerId(e.target.value ? Number(e.target.value) : '')}
                    disabled={isSendingToManager}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  >
                    <option value="">-- בחר מנהל --</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">נושא</label>
                <Input
                  value={sendToManagerSubject}
                  onChange={(e) => setSendToManagerSubject(e.target.value)}
                  placeholder="נושא האימייל"
                  disabled={isSendingToManager}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">תוכן ההודעה (הקישורים יתווספו בסוף)</label>
                <Textarea
                  value={sendToManagerBody}
                  onChange={(e) => setSendToManagerBody(e.target.value)}
                  placeholder="תוכן האימייל"
                  rows={4}
                  disabled={isSendingToManager}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleSendToManagerCancel} disabled={isSendingToManager}>
                  ביטול
                </Button>
                <Button
                  onClick={handleSendToManager}
                  disabled={isSendingToManager || !selectedManagerId || managers.length === 0}
                >
                  {isSendingToManager ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      שולח...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      שלח
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
